import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faAngleDown,
  faAngleUp,
  faUser,
  faSignInAlt,
  faImages,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthContext";
import { getInformation } from "../services/api";
import { Information } from "../types/types";
import { resolveMediaUrl } from "../utils/media";

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [information, setInformation] = useState<Information | null>(null);
  const [loading, setLoading] = useState(true); // Added loading state
  const { isAuthenticated, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLUListElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchInformation();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchInformation = async () => {
    try {
      setLoading(true);
      const response = await getInformation();
      if (response.data && response.data.length > 0) {
        setInformation(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching information:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleDropdownMouseEnter = (dropdown: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveDropdown(dropdown);
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    closeMenu();
  };

  const handleLanguageToggle = () => {
    toggleLanguage();
  };

  const handleSectionNavigation = (sectionId: string) => {
    if (location.pathname === "/ministry") {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/ministry");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
    closeMenu();
  };

  const isActiveLink = (path: string) => {
    return location.pathname === path;
  };

  const getHeaderTranslation = (key: string) => {
    const translations: Record<string, { ar: string; fr: string }> = {
      home: { ar: "الرئيسية", fr: "Accueil" },
      ministry: { ar: "الوزارة", fr: "Ministère" },
      aboutMinistry: { ar: "معالي الوزير", fr: "À propos" },
      minister: { ar: "الوزير", fr: "Ministre" },
      tasks: { ar: "المهام", fr: "Missions" },
      structure: { ar: "الهيكلة الإدارية", fr: "Structure" },
      news: { ar: "الأخبار", fr: "Actualités" },
      announcements: { ar: "إعلانات ", fr: "Annonces" },
      legislation: { ar: "التشريعات", fr: "Législation" },
      procedures: { ar: "الإجراءات والخدمات", fr: "Procédures et Services" },
      contact: { ar: "تواصل معنا", fr: "Contact" },
      login: { ar: "لوحة التحكم", fr: "Administration" },
      logout: { ar: "تسجيل الخروج", fr: "Déconnexion" },
      photos: { ar: "الصور", fr: "Photos" },
      videos: { ar: "الفيديوهات", fr: "Vidéos" },
      projects: { ar: "المشاريع", fr: "Projets" },
    };

    return translations[key]?.[language] || key;
  };

  const getDirectionClass = () => {
    return language === "ar" ? "rtl" : "ltr";
  };

  const getTextAlignClass = () => {
    return language === "ar" ? "text-right" : "text-left";
  };

  // Logo loader component
  const LogoLoader = () => (
    <div className="flex items-center justify-center bg-white/10 rounded-full border-2 border-white/30 shadow-md">
      <div className="animate-pulse flex items-center justify-center">
        <div className="h-20 w-20 md:h-16 md:w-16 flex items-center justify-center">
          <div className="h-12 w-12 md:h-10 md:w-10 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
        </div>
      </div>
    </div>
  );

  // Loading skeleton for ministry name
  const MinistryNameLoader = () => (
    <div className={`${getTextAlignClass()} space-y-2`}>
      {/* Republic name skeleton */}
      <div className="h-3 w-48 bg-white/30 rounded animate-pulse"></div>
      {/* Ministry name skeleton */}
      <div className="h-5 w-64 bg-white/40 rounded animate-pulse"></div>
    </div>
  );

  // Don't show header in admin or login pages
  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  return (
    <header
      className={`bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white shadow-custom sticky top-0 z-50 border-b-4 border-[#ce1f26] ${getDirectionClass()}`}
    >
      {/* Top Section with Logo and Country Name - Desktop only */}
      <div className="hidden md:block border-b border-white/20">
        <div className="container mx-auto px-6 py-3 flex justify-between items-center">
          {/* Left side - Logo and Ministry Name */}
          <div className="flex items-center gap-4">
            {loading ? (
              <LogoLoader />
            ) : (
              <Link to="/" onClick={closeMenu}>
                <img
                  src={
                    information?.logo
                      ? resolveMediaUrl(information.logo)
                      : "https://via.placeholder.com/80x80/00a95c/FFFFFF?text=MO"
                  }
                  alt="Ministry Logo"
                  className="h-20 w-20 rounded-full object-cover border-2 border-white/30 shadow-md transition-transform hover:scale-105"
                />
              </Link>
            )}
            <div className={getTextAlignClass()}>
              {loading ? (
                <MinistryNameLoader />
              ) : (
                <>
                  {/* Republic name first */}
                  <p className="text-sm font-semibold text-white/90">
                    {language === "ar"
                      ? "الجمهورية الإسلامية الموريتانية"
                      : "République Islamique de Mauritanie"}
                  </p>
                  {/* Ministry name below - Static translations */}
                  <h1 className="text-xl font-bold mt-1">
                    {language === "ar"
                      ? "وزارة العقارات وأملاك الدولة والإصلاح العقاري"
                      : "Ministère des Domaines et des Affaires Foncières et de la Réforme Foncière"}
                  </h1>
                </>
              )}
            </div>
          </div>

          {/* Right side - Language switcher and login (desktop) */}
          <div className="flex items-center gap-4">
            {/* Admin Dashboard/Login Button - Desktop */}
            {isAuthenticated ? (
              <Link
                to="/admin/dashboard"
                className="flex items-center justify-center bg-mauritania-gold hover:bg-mauritania-gold/80 transition-colors rounded-full w-10 h-10 shadow-md border-2 border-white/30"
                title={getHeaderTranslation("login")}
                onClick={closeMenu}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-lg text-mauritania-green-dark"
                />
              </Link>
            ) : (
              <Link
                to="/admin/login"
                className="flex items-center justify-center bg-white/20 hover:bg-white/30 transition-colors rounded-full w-10 h-10"
                title={getHeaderTranslation("login")}
                onClick={closeMenu}
              >
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-lg"
                />
              </Link>
            )}

            {/* Language Switcher */}
            <button
              onClick={handleLanguageToggle}
              className="flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors rounded-full border-2 border-white/30 hover:border-white/50 px-4 py-2 min-w-[120px]"
              title={
                language === "ar" ? "Switch to French" : "Changer à l'Arabe"
              }
            >
              <span className="text-sm font-semibold tracking-wide">
                {language === "ar" ? "Français" : "العربية"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Header with Navigation */}
      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        {/* Mobile Logo and Title */}
        <div className="md:hidden flex items-center gap-4">
          {loading ? (
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
                <div className="h-8 w-8 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
              </div>
              <div className={`${getTextAlignClass()} space-y-2`}>
                <div className="h-3 w-32 bg-white/30 rounded animate-pulse"></div>
                <div className="h-4 w-40 bg-white/40 rounded animate-pulse"></div>
              </div>
            </div>
          ) : (
            <>
              <Link to="/" onClick={closeMenu}>
                <img
                  src={
                    information?.logo
                      ? resolveMediaUrl(information.logo)
                      : "https://via.placeholder.com/70x70/00a95c/FFFFFF?text=MO"
                  }
                  alt="Ministry Logo"
                  className="h-16 w-16 rounded-full object-cover border-2 border-white/30 shadow-md"
                />
              </Link>
              <div className={getTextAlignClass()}>
                <p className="text-xs font-semibold text-white/90">
                  {language === "ar"
                    ? "الجمهورية الإسلامية الموريتانية"
                    : "République Islamique de Mauritanie"}
                </p>
                <h1 className="text-base font-bold mt-1 leading-tight">
                  {language === "ar"
                    ? "وزارة العقارات وأملاك الدولة والإصلاح العقاري"
                    : "Ministère des Domaines et des Affaires Foncières et de la Réforme Foncière"}
                </h1>
              </div>
            </>
          )}
        </div>

        {/* Mobile menu toggle */}
        <div className="md:hidden flex items-center gap-4">
          {/* Admin Dashboard/Login Button - Mobile */}
          {isAuthenticated ? (
            <Link
              to="/admin/dashboard"
              className="flex items-center justify-center bg-mauritania-gold transition-colors rounded-full w-10 h-10 shadow-md border-2 border-white/30"
              title={getHeaderTranslation("login")}
              onClick={closeMenu}
            >
              <FontAwesomeIcon
                icon={faUser}
                className="text-lg text-mauritania-green-dark"
              />
            </Link>
          ) : (
            <Link
              to="/admin/login"
              className="flex items-center justify-center bg-white/20 hover:bg-white/30 transition-colors rounded-full w-10 h-10"
              title={getHeaderTranslation("login")}
              onClick={closeMenu}
            >
              <FontAwesomeIcon
                icon={faUser}
                className="text-lg"
              />
            </Link>
          )}

          {/* Language Switcher - Mobile */}
          <button
            onClick={handleLanguageToggle}
            className="flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors rounded-full border-2 border-white/30 hover:border-white/50 px-3 py-1"
            title={language === "ar" ? "Switch to French" : "Changer à l'Arabe"}
          >
            <span className="text-xs font-semibold">
              {language === "ar" ? "FR" : "AR"}
            </span>
          </button>

          <button className="p-2" onClick={toggleMenu}>
            <FontAwesomeIcon
              icon={isMenuOpen ? faTimes : faBars}
              size="lg"
              className="text-xl"
            />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav
          className={`${
            isMenuOpen ? "right-0" : "-right-full"
          } fixed md:static top-0 h-screen md:h-auto w-3/4 md:w-auto bg-mauritania-green-dark md:bg-transparent transition-all duration-300 ease-in-out md:flex md:items-center z-40 pt-20 md:pt-0 px-6 md:px-0 ${
            language === "ar"
              ? "md:space-x-reverse md:gap-12"
              : "md:space-x-12 md:gap-12"
          }`}
        >
          <ul
            className={`flex flex-col md:flex-row gap-8 md:gap-12 ${
              language === "ar" ? "text-right" : "text-left"
            }`}
          >
            <li>
              <Link
                to="/"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("home")}
              </Link>
            </li>

            <li>
              <Link
                to="/news"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/news")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("news")}
              </Link>
            </li>

            <li>
              <Link
                to="/minister"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/minister")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("ministry")}
              </Link>
            </li>
            <li>
              <Link
                to="/projects"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/projects")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("projects")}
              </Link>
            </li>
            <li>
              <Link
                to="/procedures"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/procedures")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("procedures")}
              </Link>
            </li>
            <li>
              <Link
                to="/announcements"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/announcements")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("announcements")}
              </Link>
            </li>

            <li>
              <Link
                to="/contact"
                className={`hover:text-[#ce1f26] transition pb-1 text-3xl md:text-2xl font-medium block py-4 md:py-0 ${
                  isActiveLink("/contact")
                    ? "border-b-2 md:border-b-3 border-[#ce1f26] font-semibold"
                    : ""
                }`}
                onClick={closeMenu}
              >
                {getHeaderTranslation("contact")}
              </Link>
            </li>

            {isAuthenticated ? (
              <li className="md:hidden">
                <button
                  onClick={handleLogout}
                  className={`hover:text-[#ce1f26] transition text-3xl font-medium py-4 w-full flex items-center gap-3 ${
                    language === "ar" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faSignInAlt}
                    className="rotate-180 text-2xl"
                  />
                  <span>{getHeaderTranslation("logout")}</span>
                </button>
              </li>
            ) : (
              <li className="md:hidden">
                <Link
                  to="/admin/login"
                  className={`hover:text-[#ce1f26] transition pb-1 text-3xl font-medium py-4 flex items-center gap-4 ${
                    isActiveLink("/admin/login")
                      ? "border-b-2 border-[#ce1f26] font-semibold"
                      : ""
                  } ${language === "ar" ? "flex-row-reverse" : "flex-row"}`}
                  onClick={closeMenu}
                >
                  <FontAwesomeIcon icon={faUser} className="text-2xl" />
                  <span>{getHeaderTranslation("login")}</span>
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Desktop Admin Logout */}
        {isAuthenticated && (
          <div className="hidden md:flex items-center">
            <button
              onClick={handleLogout}
              className={`hover:text-[#ce1f26] transition text-xl font-medium flex items-center gap-3 ${
                language === "ar" ? "flex-row-reverse" : "flex-row"
              }`}
            >
              <FontAwesomeIcon
                icon={faSignInAlt}
                className="rotate-180 text-lg"
              />
              <span>{getHeaderTranslation("logout")}</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
